<template>
  <Dialog v-model:open="isModalOpen" class="mx-auto">
    <DialogContent
      class="!rounded-none md:!rounded-3xl max-w-[800px] md:w-11/12 w-full p-0 border-none h-full md:h-auto !bg-transparent"
      @interact-outside="(e) => e.preventDefault()"
    >
      <div class="flex min-h-full items-center justify-center mx-6">
        <div
          class="w-full sm:max-w-xl md:max-w-3xl lg:max-w-max bg-white px-8 py-6 rounded-xl relative"
        >
          <div
            class="bg-white rounded-lg h-8 w-8 absolute top-0 -right-10 flex justify-center items-center cursor-pointer"
            @click="oncloseModal"
          >
            <Icon class="w-5 h-5" icon="mingcute:close-fill"></Icon>
          </div>
          <div class="flex flex-col gap-y-1">
            <span class="text-heading-sm">Generations</span>
            <span class="text-body-md text-text-secondary">
              See and download your generations at an instant
            </span>
          </div>
          <div
            class="mt-6 border border-border-primary p-6 flex gap-x-6 rounded-xl bg-background-secondary"
          >
            <div class="w-full flex justify-center">
              <div class="w-[calc(100%-20%)] flex flex-col justify-between">
                <Carousel
                  class="relative flex items-center justify-center"
                  :loop="true"
                  @init-api="(val) => (emblaMainApi = val)"
                >
                  <CarouselContent>
                    <template v-for="items in selectedModalImage">
                      <CarouselItem
                        v-for="(item, index) in items.images"
                        :key="index"
                      >
                        <div class="w-full">
                          <img
                            :src="item"
                            alt="preview image"
                            class="rounded-xl w-auto h-auto object-cover"
                          />
                        </div>
                      </CarouselItem>
                    </template>
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>

                <Carousel
                  class="relative w-full"
                  :loop="true"
                  @init-api="(val) => (emblaThumbnailApi = val)"
                >
                  <CarouselContent class="flex gap-3 mt-3 ml-0">
                    <template v-for="(items, id) in selectedModalImage">
                      <CarouselItem
                        v-for="(item, index) in items.images"
                        :key="index"
                        class="pl-0 basis-1/5 cursor-pointer"
                        @click="onThumbClick(id)"
                      >
                        <div
                          class="rounded-lg overflow-hidden flex justify-center"
                          :class="id === selectedIndex ? '' : 'opacity-50'"
                        >
                          <img
                            class="object-cover w-auto"
                            :src="item"
                            alt="list images"
                          />
                        </div>
                      </CarouselItem>
                    </template>
                  </CarouselContent>
                </Carousel>
              </div>
            </div>
            <div class="flex flex-col justify-between gap-y-4">
              <div>
                <Button
                  class="text-sm flex gap-x-2 w-full"
                  @click="downloadBGImages"
                >
                  <span>Download</span>
                  <Icon
                    v-if="bgDownloadLoader"
                    icon="svg-spinners:ring-resize"
                  ></Icon>
                  <Icon v-else icon="solar:download-minimalistic-linear"></Icon>
                </Button>
                <div
                  class="max-w-52 w-max lg:min-w-52 border border-border-primary rounded-lg py-4 px-5 mt-5 flex flex-col gap-y-4 bg-white"
                >
                  <div class="flex flex-col gap-y-1">
                    <span class="text-text-secondary text-label-lg">Size:</span>
                    <span class="text-label-lg">1024 x 1024 px</span>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <span class="text-text-secondary text-sm">Prompt:</span>
                    <span class="text-sm">
                      {{ selectedModalImage?.[selectedIndex]?.prompt }}
                    </span>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <span class="text-text-secondary text-label-lg">
                      Credits:
                    </span>
                    <span class="text-label-lg">2</span>
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <span class="text-text-secondary text-label-lg">
                      Stored Till:
                    </span>
                    <span class="text-label-lg">
                      {{ selectedModalImage?.[selectedIndex]?.expireOn }}
                    </span>
                  </div>
                </div>
              </div>
              <div
                class="w-14 border border-border-primary rounded-lg p-3 bg-white ml-auto"
              >
                <img
                  :src="selectedModalImage?.[selectedIndex]?.thumbnail"
                  alt="original image"
                  class="rounded-sm overflow-hidden"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { DialogContent, Dialog } from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Button from "@/components/ui/button/Button.vue";
import { useSubscription } from "@vueuse/rxjs";
import { openPhotoshootModal, closePhotoshootModal } from "@/lib/utils.js";
import { Icon } from "@iconify/vue";

const isModalOpen = ref(false);
const bgDownloadLoader = ref(false);
const emblaMainApi = ref(null);
const emblaThumbnailApi = ref(null);

const props = defineProps({
  selectedModalImage: Array,
});

const selectedIndex = defineModel("selectedIndex", { type: Number });

useSubscription(
  closePhotoshootModal.subscribe(() => {
    isModalOpen.value = false;
  }),
);

useSubscription(
  openPhotoshootModal.subscribe(() => {
    isModalOpen.value = true;
  }),
);

const oncloseModal = () => {
  isModalOpen.value = false;
};

function onSelect() {
  if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
  selectedIndex.value = emblaMainApi.value.selectedScrollSnap();
  emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap());
}

function onThumbClick(index) {
  selectedIndex.value = index;
  if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
  emblaMainApi.value.scrollTo(index);
}

async function downloadBGImages() {
  bgDownloadLoader.value = true;
  const imgUrl = props.selectedModalImage[selectedIndex.value].images[0];

  // Fix for showing download loader
  await new Promise((resolve) => setTimeout(resolve, 1000));

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = new Image();
  img.src = `${imgUrl}?${new Date().getTime()}`;
  img.setAttribute("crossOrigin", "");

  img.onload = () => {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(
      img,
      0,
      0,
      img.naturalWidth,
      img.naturalHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = props.selectedModalImage?.[selectedIndex.value]?.prompt;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  bgDownloadLoader.value = false;
}

watch(
  emblaMainApi,
  (newVal) => {
    if (!newVal) return;

    newVal.scrollTo(selectedIndex.value);
    onSelect();

    newVal.on("select", onSelect);
    newVal.on("reInit", onSelect);
  },
  { deep: true },
);
</script>
